import { isEqual, map } from 'lodash';
import { useEffect, useMemo } from 'react';

import { useCalendarContext } from 'pages/processor/calendar/CalendarProvider';

import { resolveTagFilters } from './resolve-tag-filters';
import { useJobTags } from './use-job-tags';

/**
 * The calendar's tag filters as they should be applied to jobs — see
 * `resolveTagFilters` for why the URL's list can't be used directly.
 *
 * Also repairs the URL: once the vendor's tags have loaded, ids that no
 * longer name an enabled tag are written out of it, so a stale bookmark heals
 * itself instead of carrying a dead filter around. It is deliberately NOT
 * rewritten while the module reads as off — that is also what an unloaded
 * session looks like, and a flag flipped back on should find the bookmark
 * intact.
 * @returns the tag ids to filter jobs by; empty means no filter.
 */
export function useAppliedTagFilters(): string[] {
  const { tagFilters, setTagFilters } = useCalendarContext();
  const { enabledTags, jobTagsEnabled, loaded } = useJobTags();

  const applied = useMemo(
    () =>
      resolveTagFilters({
        tagFilters,
        enabledTagIds: map(enabledTags, '_id'),
        jobTagsEnabled,
        tagsLoaded: loaded,
      }),
    [tagFilters, enabledTags, jobTagsEnabled, loaded],
  );

  useEffect(() => {
    if (jobTagsEnabled && loaded && !isEqual(applied, tagFilters)) {
      setTagFilters(applied);
    }
  }, [applied, tagFilters, jobTagsEnabled, loaded, setTagFilters]);

  return applied;
}
